import clsx from 'clsx';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';

import { DiscoveryStatuses, type IntelligentDraftResponse } from '../api';
import { AbandonDraftButton } from '../components/AbandonDraftButton';
import { StepperIcon } from '../components/StepperIcon';
import { useDraft } from '../hooks';

interface StepDescriptor {
  id: string;
  label: string;
  /** Last path segment after the draftId. */
  routeSuffix: string;
  /** Matches a substring in the current pathname. */
  pathMatcher: (path: string, hasDraft: boolean) => boolean;
}

const STEPS: ReadonlyArray<StepDescriptor> = [
  {
    id: 'upload',
    label: '1. Subir',
    routeSuffix: 'upload',
    pathMatcher: (path, hasDraft) => path.endsWith('/upload') || !hasDraft,
  },
  {
    id: 'analyzing',
    label: '2. Analizar',
    routeSuffix: 'analyzing',
    pathMatcher: (path) => path.endsWith('/analyzing'),
  },
  {
    id: 'review',
    label: '3. Revisar',
    routeSuffix: 'review',
    pathMatcher: (path) => path.endsWith('/review'),
  },
  {
    id: 'maturity',
    label: '4. Madurez',
    routeSuffix: 'maturity',
    pathMatcher: (path) => path.endsWith('/maturity'),
  },
  {
    id: 'publish',
    label: '5. Publicar',
    routeSuffix: 'publish',
    pathMatcher: (path) => path.endsWith('/publish'),
  },
];

/**
 * Returns the index of the highest step that the draft has reached, based on
 * its current status. The Upload step is always reachable (it can show a
 * mounted draft for resumption). The Analyzing step is reachable while the
 * orchestrator is still working OR has finished — once a draft exists the
 * user can revisit it. Review/Maturity/Publish unlock progressively.
 */
function reachableIndex(draft: IntelligentDraftResponse | undefined): number {
  if (draft === undefined) {
    return 0;
  }
  switch (draft.status) {
    // Created = gathering input (e.g. a re-education fallback draft awaiting uploads): only the
    // upload step is reachable — enabling "Analizar" here bounces through its redirect effect
    // into the review empty-state dead-end.
    case DiscoveryStatuses.Created:
      return 0;
    case DiscoveryStatuses.Analyzing:
    case DiscoveryStatuses.Failed:
      return 1;
    case DiscoveryStatuses.ProposalReady:
      return 2;
    case DiscoveryStatuses.InReview:
    case DiscoveryStatuses.Testing:
      return 3;
    case DiscoveryStatuses.ReadyToPublish:
    case DiscoveryStatuses.Published:
      return 4;
    case DiscoveryStatuses.Abandoned:
    default:
      return 0;
  }
}

/**
 * Discovery wizard shell. The route table mounts a different child step under
 * the same wrapper so the stepper stays visible while users navigate forward
 * and backward in the flow.
 *
 * The wizard intentionally does not own the draft state — each step uses
 * `useDraft` / `useDraftPolling` directly so React Query is the single source
 * of truth and the UI stays in sync after server-side mutations.
 *
 * The stepper is interactive: completed and current steps are clickable so
 * users can revisit any prior phase without losing context. Future steps
 * remain disabled until the draft progresses far enough.
 */
export function DiscoveryWizard(): JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const { draftId } = useParams<{ draftId: string }>();
  const hasDraft = draftId !== undefined;
  const { data: draft } = useDraft(draftId);

  const activeIndex = STEPS.findIndex((step) => step.pathMatcher(location.pathname, hasDraft));
  const maxReachable = reachableIndex(draft);

  // The published step is rendered via the publish endpoint but stays under the
  // same route shell briefly. We hide the abandon action when there is no draft
  // to abandon yet (upload step) and on the publish step itself.
  const showAbandon =
    hasDraft && draftId !== undefined && !location.pathname.endsWith('/publish');

  const handleStepClick = (step: StepDescriptor): void => {
    if (!hasDraft || draftId === undefined) {
      return;
    }
    if (step.id === 'upload') {
      navigate(`/document-types/discover/${encodeURIComponent(draftId)}/upload`);
      return;
    }
    navigate(`/document-types/discover/${encodeURIComponent(draftId)}/${step.routeSuffix}`);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between gap-2">
        <h1 className="sr-only">Asistente de descubrimiento</h1>
        {showAbandon && draftId !== undefined ? (
          <div className="ml-auto">
            <AbandonDraftButton draftId={draftId} />
          </div>
        ) : null}
      </div>
      <nav aria-label="Pasos del asistente">
        <ol className="card flex flex-wrap items-center gap-2 p-3 text-xs">
          {STEPS.map((step, i) => {
            const isActive = i === activeIndex;
            const isCompleted = activeIndex >= 0 && i < activeIndex;
            const isEnabled = hasDraft && i <= Math.max(maxReachable, activeIndex);
            const baseClasses = clsx(
              'rounded-full px-3 py-1 font-medium transition-colors',
              isActive && 'bg-primary text-white',
              !isActive && isCompleted && 'bg-emerald-100 text-emerald-800',
              !isActive && !isCompleted && 'bg-slate-100 text-slate-500',
              isEnabled && !isActive ? 'cursor-pointer hover:bg-slate-200' : '',
              !isEnabled && !isActive ? 'opacity-60' : '',
            );
            return (
              <li key={step.id} className="flex items-center gap-2">
                <button
                  type="button"
                  className={clsx(baseClasses, 'inline-flex items-center gap-1.5')}
                  onClick={() => handleStepClick(step)}
                  disabled={!isEnabled}
                  data-testid={`wizard-step-${step.id}`}
                  data-active={isActive || undefined}
                  aria-current={isActive ? 'step' : undefined}
                >
                  <StepperIcon
                    stepKey={step.id}
                    active={isActive}
                    className={clsx('h-3.5 w-3.5', isActive && step.id === 'analyzing' && 'animate-spin')}
                  />
                  <span>{step.label}</span>
                </button>
                {i < STEPS.length - 1 ? (
                  <span aria-hidden className="text-slate-300">
                    →
                  </span>
                ) : null}
              </li>
            );
          })}
        </ol>
      </nav>

      <div key={location.pathname} className="animate-fade-in">
        <Outlet />
      </div>
    </div>
  );
}
